import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Autocomplete,
  TextField,
  Tab,
  Tabs,
  IconButton,
  Typography,
  CircularProgress,
} from '@mui/material';
import { withTranslation } from 'react-i18next';
import axios from 'axios';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import AddIcon from '@mui/icons-material/Add';
import NewCustomerForm, { EMPTY_NEW_CUSTOMER, isNewCustomerValid } from '../shared/NewCustomerForm';
import { customerLabel } from '../../utils/customer';

function TabPanel({ children, value, index, ...props }) {
  return (
    <Box role="tabpanel" hidden={value !== index} {...props} sx={{ pt: 2 }}>
      {value === index && children}
    </Box>
  );
}

function AssignCustomerDialog({ t, open, handleClose, setMaintenance, maintenance }) {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [tab, setTab] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // New customer form
  const [newCustomer, setNewCustomer] = useState({ ...EMPTY_NEW_CUSTOMER });

  // Load all customers (for initial load or when search is cleared)
  const loadAllCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/customers/?page_size=50');
      // Handle paginated response
      let customerList = response.data.results || response.data;
      customerList = Array.isArray(customerList) ? customerList : [];
      // Ensure the current customer is in the list
      if (maintenance?.customer && !customerList.find(c => c.id === maintenance.customer.id)) {
        customerList = [maintenance.customer, ...customerList];
      }
      setCustomers(customerList);
    } catch (err) {
      console.error('Error loading customers:', err);
    } finally {
      setLoading(false);
    }
  }, [maintenance?.customer]);

  // Debounced search
  const searchCustomers = useCallback(async (query) => {
    if (!query || query.length < 2) {
      // Reload all customers when search is cleared
      loadAllCustomers();
      return;
    }

    setSearchLoading(true);
    try {
      const response = await axios.get(`/api/customers/?search=${encodeURIComponent(query)}&typeahead=true`);
      // Typeahead returns unpaginated array
      const customersData = response.data.results || response.data;
      setCustomers(Array.isArray(customersData) ? customersData : []);
    } catch (err) {
      console.error('Error searching customers:', err);
    } finally {
      setSearchLoading(false);
    }
  }, [loadAllCustomers]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput) {
        searchCustomers(searchInput);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput, searchCustomers]);

  // Load initial customers when dialog opens
  useEffect(() => {
    if (open) {
      setTab(0);
      setError(null);
      // Set the currently assigned customer as selected
      setSelectedCustomer(maintenance?.customer || null);
      setSearchInput('');
      setNewCustomer({ ...EMPTY_NEW_CUSTOMER });

      // Load initial set of customers
      loadAllCustomers();
    }
  }, [open, loadAllCustomers]);

  const handleAssignExisting = async () => {
    if (!selectedCustomer) return;

    setError(null);
    setSubmitting(true);
    try {
      const response = await axios.patch(`/api/maintenances/${maintenance.id}/set_customer/`, {
        customer_id: selectedCustomer.id,
      });
      setMaintenance(response.data);
      handleClose();
    } catch (err) {
      setError(t('Could not assign customer. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateAndAssign = async () => {
    setError(null);
    setSubmitting(true);
    try {
      // Create new customer (name is auto-computed by backend)
      const createResponse = await axios.post('/api/customers/', newCustomer);
      const createdCustomer = createResponse.data;

      // Assign to maintenance
      const response = await axios.patch(`/api/maintenances/${maintenance.id}/set_customer/`, {
        customer_id: createdCustomer.id,
      });
      setMaintenance(response.data);
      handleClose();
    } catch (err) {
      setError(t('Could not assign customer. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const getOptionLabel = (option) => {
    if (!option) return '';
    if (!option.name) return customerLabel(option);
    let label = option.name;
    const extras = [];
    if (option.business_id) extras.push(option.business_id);
    if (option.email) extras.push(option.email);
    if (option.phone) extras.push(option.phone);
    if (extras.length > 0) {
      label += ` (${extras.join(' | ')})`;
    }
    return label;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: '12px', sm: '16px' },
          m: { xs: 2, sm: 4 },
          maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 3,
          pb: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: '20px',
            fontWeight: 600,
            color: 'text.primary',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {t('Assign customer')}
        </Typography>
        <IconButton onClick={handleClose} sx={{ color: 'text.disabled' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, sm: 3 }, pt: 1 }}>
        <Tabs
          value={tab}
          onChange={(e, v) => { setError(null); setTab(v); }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 500,
              textTransform: 'none',
              minWidth: { xs: 'auto', sm: 90 },
              px: { xs: 1.5, sm: 2 },
              fontSize: { xs: '13px', sm: '14px' },
            },
          }}
        >
          <Tab icon={<PersonIcon />} iconPosition="start" label={t('Select existing')} />
          <Tab icon={<AddIcon />} iconPosition="start" label={t('Create new')} />
        </Tabs>

        {error && (
          <Typography color="error" sx={{ mt: 2, fontSize: '14px' }}>
            {error}
          </Typography>
        )}

        {/* Existing Customer Tab */}
        <TabPanel value={tab} index={0}>
          <Autocomplete
            options={customers}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            loading={searchLoading}
            value={selectedCustomer}
            onChange={(e, value) => setSelectedCustomer(value)}
            filterOptions={(x) => x}
            onInputChange={(e, value, reason) => {
              // Only update searchInput when user types, not when value is set programmatically
              if (reason === 'input') {
                setSearchInput(value);
              }
            }}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id}>
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {customerLabel(option)}
                    {option.type === 'company' && (
                      <BusinessIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle', color: 'text.secondary' }} />
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[option.business_id, option.email, option.phone].filter(Boolean).join(' | ')}
                  </Typography>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                autoComplete="off"
                label={t('Search customers')}
                placeholder={t('Search by name, email, phone, or business ID...')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {searchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                  },
                }}
              />
            )}
          />

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{ borderRadius: '10px', px: 3 }}
            >
              {t('Cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleAssignExisting}
              disabled={!selectedCustomer || submitting}
              sx={{
                borderRadius: '10px',
                px: 3,
                bgcolor: 'secondary.main',
                '&:hover': { bgcolor: 'secondary.light' },
              }}
            >
              {submitting ? <CircularProgress size={20} /> : <span>{t('Assign')}</span>}
            </Button>
          </Box>
        </TabPanel>

        {/* New Customer Tab */}
        <TabPanel value={tab} index={1}>
          <NewCustomerForm
            t={t}
            value={newCustomer}
            onChange={setNewCustomer}
            fieldSx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
          />

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{ borderRadius: '10px', px: 3 }}
            >
              {t('Cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateAndAssign}
              disabled={!isNewCustomerValid(newCustomer) || submitting}
              sx={{
                borderRadius: '10px',
                px: 3,
                bgcolor: 'secondary.main',
                '&:hover': { bgcolor: 'secondary.light' },
              }}
            >
              {submitting ? <CircularProgress size={20} /> : <span>{t('Create and assign')}</span>}
            </Button>
          </Box>
        </TabPanel>
      </DialogContent>
    </Dialog>
  );
}

export default withTranslation()(AssignCustomerDialog);
